/**
 * What is happening now and next, across every sport.
 *
 * The match-day path is three screens — here, the fixture, the start list — and
 * this is the one that has to be fast. It is deliberately not a dashboard of
 * counts: nobody opens a data-entry tool to find out how many teams exist. They
 * open it because something is starting.
 *
 * "Today" is now the *browser's* today rather than the server's, which is the
 * answer the person reading it actually wants.
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";

import { Card, Empty, PageHead, Pill, Problem, Section, Table, Td, Th } from "@/components/kit";
import { listFixtures } from "@/lib/api/client";
import type { FixtureRow } from "@/lib/api/types";
import { displayName } from "@/lib/format";
import { useApi } from "@/spa/useApi";

/** How a fixture's status should read at a glance. Live first, because that is
 * the only one that means "someone is waiting". */
function tone(status: string): "live" | "good" | "warn" | "neutral" {
  if (status === "LIVE" || status === "IN_PROGRESS") return "live";
  if (status === "COMPLETED") return "good";
  if (status === "POSTPONED" || status === "ABANDONED" || status === "CANCELLED") return "warn";
  return "neutral";
}

function clock(when: string | null | undefined): string {
  if (!when) return "—";
  return new Date(when).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function Fixtures({ rows }: { rows: FixtureRow[] }) {
  return (
    <Table>
      <thead>
        <tr>
          <Th>Time</Th>
          <Th>Fixture</Th>
          <Th>Sport</Th>
          <Th>Where it sits</Th>
          <Th>Status</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <Td quiet>{clock(row.scheduledStart)}</Td>
            <Td strong>
              <Link to={`/fixtures/${row.id}`} style={{ color: "inherit", textDecoration: "none" }}>
                {row.name ?? row.code}
              </Link>
            </Td>
            <Td>{displayName(row.sportCode)}</Td>
            <Td quiet>{row.competition ?? "—"}</Td>
            <Td>
              <Pill tone={tone(row.status)} dot={tone(row.status) !== "neutral"}>
                {row.status.toLowerCase().replace(/_/g, " ")}
              </Pill>
            </Td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
}

export function TodayPage() {
  // Memoised, and not for speed: `new Date()` in the render body is a fresh
  // value every pass, so an unmemoised window would be a new dependency each
  // time and the two requests below would loop forever.
  const window = useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfTomorrow = new Date(startOfDay);
    startOfTomorrow.setDate(startOfTomorrow.getDate() + 1);
    const inAWeek = new Date(startOfDay);
    inAWeek.setDate(inAWeek.getDate() + 7);
    return { now, startOfDay, startOfTomorrow, inAWeek };
  }, []);

  const { data, loading, error } = useApi(
    () =>
      // Two windows rather than one long list: "today" is what someone is
      // working through, and "this week" is what they are checking has been set
      // up. Mixed together the second buries the first.
      Promise.all([
        listFixtures({
          from: window.startOfDay.toISOString(),
          to: window.startOfTomorrow.toISOString(),
          order: "soonest",
          limit: 100,
        }),
        listFixtures({
          from: window.startOfTomorrow.toISOString(),
          to: window.inAWeek.toISOString(),
          order: "soonest",
          limit: 25,
        }),
      ]),
    [window],
    "failed to load",
  );

  const heading = (
    <PageHead
      title="Today"
      note={window.now.toLocaleDateString([], {
        weekday: "long",
        day: "numeric",
        month: "long",
      })}
    />
  );

  if (error) return <Problem>Could not load today: {error}</Problem>;
  if (!data) {
    return (
      <>
        {heading}
        {loading && <Section title="On today">{null}</Section>}
      </>
    );
  }

  const [today, next] = data;

  return (
    <>
      {heading}

      <Section title="On today" note={`${today.total} across every sport`}>
        {today.rows.length === 0 ? (
          <Card>
            <Empty
              title="Nothing on today"
              note="Fixtures scheduled for today show here, whichever sport they belong to."
            />
          </Card>
        ) : (
          <Fixtures rows={today.rows} />
        )}
      </Section>

      {next.rows.length > 0 && (
        <Section title="Coming up" note="The next seven days">
          <Fixtures rows={next.rows} />
        </Section>
      )}
    </>
  );
}
